/**
 * vx-stories.js
 *
 * Feed : init Swiper par instance (.vx-swiper).
 *   - boucle infinie si data-loop="true" (>= 3 vidéos, posé par Liquid)
 *   - slidesPerView calculé depuis data-slides-desktop / data-slides-mobile (valeur en %)
 *   - autoplay intersection-based (preview 5s, pause hors viewport)
 *   - pas de flèches
 *
 * Modal : lecteur plein écran inchangé.
 */

(() => {
  const q  = (s, r = document) => r.querySelector(s);
  const qa = (s, r = document) => Array.from(r.querySelectorAll(s));

  const FEED_PREVIEW_DURATION = 5; // secondes de preview dans le feed

  /* ── Détection mobile ─────────────────────────────────────── */
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  /* ============================
     FEED — Swiper init
  ============================ */

  function initAllSwipers() {
    qa('.vx-swiper').forEach(el => {
      if (el.dataset.vxInit === '1') return;
      el.dataset.vxInit = '1';
      initSwiper(el);
    });
  }

  function initSwiper(el) {
    const loop         = el.dataset.loop === 'true';
    const spaceBetween = parseInt(el.dataset.spaceBetween || '12', 10);

    /*
     * slidesPerView calculé à partir du pourcentage stocké en data-slides-desktop/mobile.
     * On convertit "85" (%) → nombre de slides visibles.
     * Ex : 85% de largeur visible = environ 1.18 slides → on utilise 'auto'
     * et on laisse Swiper gérer via la largeur CSS du slide.
     *
     * Stratégie : on utilise slidesPerView: 'auto' + width en % sur .swiper-slide via CSS inline.
     * C'est la façon la plus flexible et la plus robuste avec Swiper.
     */
    const pctDesktop = parseInt(el.dataset.slidesDesktop || '85', 10);
    const pctMobile  = parseInt(el.dataset.slidesMobile  || '85', 10);

    // Appliquer la largeur % sur chaque slide réel (pas les clones Swiper)
    function applySlideWidths() {
      const pct = isMobile() ? pctMobile : pctDesktop;
      qa('.swiper-slide:not(.swiper-slide-duplicate)', el).forEach(slide => {
        slide.style.width = pct + '%';
      });
    }
    applySlideWidths();

    const swiper = new Swiper(el, {
      slidesPerView:  'auto',
      spaceBetween,
      loop,
      grabCursor:     true,
      /*
       * centeredSlides: false → le slide actif s'aligne à gauche, comportement naturel.
       * Mettre à true si on veut centrer le slide actif (style story plein écran).
       */
      centeredSlides: false,
      /*
       * Pas de navigation (flèches supprimées).
       * Pas de pagination (non demandée).
       */
      on: {
        slideChange() {
          // Pause toutes les vidéos, joue celle du slide actif
          updateFeedVideoPlayback(el, this.realIndex);
        },
        resize() {
          applySlideWidths();
        },
      },
    });

    // Init autoplay intersection — c'est lui qui déclenche loadSrc sur la première vidéo visible
    initFeedAutoplay(el);
  }

  /* ── Lazy-load src depuis data-src ──────────────────────── */
  function loadSrc(v) {
    if (v.dataset.src && !v.getAttribute('src')) v.src = v.dataset.src;
  }

  /* ── Lecture vidéo du slide actif ────────────────────────── */
  function updateFeedVideoPlayback(swiperEl, realIndex) {
    qa('.swiper-slide:not(.swiper-slide-duplicate) .vx-story-vid video', swiperEl)
      .forEach((v, i) => {
        if (i === realIndex) {
          loadSrc(v);
          v.currentTime = 0;
          v.play().catch(() => {});
        } else {
          v.pause();
          v.currentTime = 0;
        }
      });
  }

  /* ── Autoplay intersection ───────────────────────────────── */
  function initFeedAutoplay(scope) {
    const videos = qa('.vx-story-vid video', scope);
    if (!videos.length) return;

    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        const v = e.target;
        if (v.closest('.swiper-slide-duplicate')) return;
        if (e.isIntersecting) {
          loadSrc(v);
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      }),
      { threshold: 0.1 }
    );

    videos.forEach(v => {
      v.preload     = 'none';
      v.muted       = true;
      v.playsInline = true;
      v.loop        = true;
      io.observe(v);
    });
  }

  /* ============================
     CONTRÔLES MODAL (injectés par slide)
  ============================ */

  function injectCSS() {
    if (document.getElementById('vx-ctrl-css')) return;
    const s = document.createElement('style');
    s.id = 'vx-ctrl-css';
    s.textContent = `
      .vx-ctrl-overlay{position:absolute;inset:0;z-index:20;pointer-events:none}
      .vx-ctrl-center{
        position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
        display:flex;align-items:center;gap:40px;
        opacity:0;transition:opacity 240ms ease;pointer-events:none
      }
      .vx-ctrl-center.is-visible{opacity:1;pointer-events:auto}
      .vx-ctrl-btn{
        width:40px;height:40px;border-radius:50%;border:none;
        background:rgba(255,255,255,.13);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
        color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;
        flex-direction:column;gap:3px;padding:0;outline:none;
        transition:background 160ms ease,transform 120ms ease
      }
      .vx-ctrl-btn:active{transform:scale(.88);background:rgba(255,255,255,.22)}
      .vx-ctrl-btn--play{width:64px;height:64px}
      .vx-ctrl-btn svg{display:block}
      .vx-ctrl-btn__label{font-size:8px;font-weight:700;color:#fff;line-height:1;letter-spacing:.03em}

      .vx-slide-loader{
        position:absolute;inset:0;z-index:30;
        display:flex;align-items:center;justify-content:center;
        background:rgba(0,0,0,.45);opacity:0;pointer-events:none;
        transition:opacity 200ms ease;
      }
      .vx-slide-loader.is-loading{opacity:1}
      .vx-slide-loader__spinner{
        width:42px;height:42px;border-radius:50%;
        border:3px solid rgba(255,255,255,.25);border-top-color:#fff;
        animation:vx-spin .75s linear infinite
      }
      @keyframes vx-spin{to{transform:rotate(360deg)}}

      .vx-seekbar{
        position:absolute;bottom:0;left:0;right:0;z-index:25;
        padding:18px 14px 14px;
        background:linear-gradient(transparent,rgba(0,0,0,.55));
        pointer-events:auto;cursor:pointer;touch-action:none;
        user-select:none;-webkit-user-select:none;
      }
      .vx-seekbar__track{position:relative;height:4px;border-radius:2px;background:rgba(255,255,255,.28);transition:height 160ms ease;}
      .vx-seekbar:hover .vx-seekbar__track,.vx-seekbar.is-dragging .vx-seekbar__track{height:6px;}
      .vx-seekbar__fill{position:absolute;inset:0;right:auto;background:#fff;border-radius:2px;pointer-events:none;width:0%;}
      .vx-seekbar__thumb{
        position:absolute;top:50%;width:14px;height:14px;border-radius:50%;background:#fff;
        transform:translate(-50%,-50%) scale(0);transition:transform 160ms ease;
        pointer-events:none;box-shadow:0 1px 4px rgba(0,0,0,.4);
      }
      .vx-seekbar:hover .vx-seekbar__thumb,.vx-seekbar.is-dragging .vx-seekbar__thumb{transform:translate(-50%,-50%) scale(1);}
      .vx-seekbar__time{display:flex;justify-content:space-between;margin-top:7px;font-size:11px;font-weight:600;color:rgba(255,255,255,.8);letter-spacing:.04em;pointer-events:none;font-family:'Open Sans',sans-serif;}

      @media(max-width:768px){
        .vx-modal__rail{display:flex!important;flex-direction:column!important;width:100%!important;height:calc(100dvh * var(--vx-story-count,1))!important;gap:0!important;}
        .vx-modal__slide{width:100%!important;height:100dvh!important;flex-shrink:0!important;padding:0!important;}
        .vx-modal__slideInner{border-radius:0!important;}
      }

      .vx-tap-icon{
        position:absolute;top:50%;left:50%;
        width:76px;height:76px;border-radius:50%;
        background:rgba(255,255,255,.22);
        backdrop-filter:blur(18px) saturate(180%);-webkit-backdrop-filter:blur(18px) saturate(180%);
        display:flex;align-items:center;justify-content:center;
        pointer-events:none;z-index:22;
        animation:vx-tap-pop 620ms cubic-bezier(0.4,0,0.2,1) forwards;
      }
      .vx-tap-icon svg{width:30px;height:30px;fill:#fff;filter:drop-shadow(0 1px 6px rgba(0,0,0,.25));}
      @keyframes vx-tap-pop{
        0%  {opacity:.9;transform:translate(-50%,-50%) scale(.6);}
        22% {opacity:1; transform:translate(-50%,-50%) scale(1.18);}
        45% {opacity:1; transform:translate(-50%,-50%) scale(.96);}
        65% {opacity:.9;transform:translate(-50%,-50%) scale(1);}
        100%{opacity:0; transform:translate(-50%,-50%) scale(1);}
      }

      .vx-miniplayer__inner{
        position:relative;
        width:320px;
        height:450px;
        border-radius:12px;
        overflow:hidden;
        background:#000;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .vx-miniplayer__inner video{width:100%;height:100%;object-fit:contain;}
      .vx-miniplayer__controls{
        position:absolute;top:8px;right:8px;display:flex;gap:6px;z-index:2;
      }
      .vx-miniplayer__btn{
        width:32px;height:32px;border-radius:50%;background:rgba(255,255,255,.2);border:none;
        backdrop-filter:blur(16px) saturate(180%);-webkit-backdrop-filter:blur(16px) saturate(180%);
        cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;
        transition:background .2s ease,transform .2s ease;padding:0;flex-shrink:0;
      }
      .vx-miniplayer__btn:hover{background:rgba(255,255,255,.32);transform:scale(1.08);}
      .vx-miniplayer__btn svg{width:16px;height:16px;display:block;}
      .vx-miniplayer__btn-sound .mute-slash{opacity:0;transition:opacity .25s ease;}
      .vx-miniplayer__btn-sound.is-muted .mute-slash{opacity:1;}

      @media(max-width:768px){
        .vx-miniplayer__inner{
          width:240px;
          height:340px;
        }
        .vx-miniplayer__btn{width:36px;height:36px;}
        .vx-miniplayer__btn svg{width:18px;height:18px;}
      }
    `;
    document.head.appendChild(s);
  }

  const SVG = {
    play:    `<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>`,
    pause:   `<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`,
    rewind:  `<svg width="19" height="19" viewBox="0 0 24 24" fill="#fff"><path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>`,
    forward: `<svg width="19" height="19" viewBox="0 0 24 24" fill="#fff"><path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>`,
  };

  const ctrlTimers = new WeakMap();
  const mk = (tag, cls, html = '') => { const el = document.createElement(tag); if (cls) el.className = cls; if (html) el.innerHTML = html; return el; };
  const fmtTime = sec => { if (!isFinite(sec) || isNaN(sec)) return '0:00'; const m = Math.floor(sec/60), s = Math.floor(sec%60); return `${m}:${s.toString().padStart(2,'0')}`; };
  const showLoader = slide => q('.vx-slide-loader', slide)?.classList.add('is-loading');
  const hideLoader = slide => q('.vx-slide-loader', slide)?.classList.remove('is-loading');

  function createSeekbar(slide) {
    const v = q('video', slide);
    const inner = q('.vx-modal__slideInner', slide) || slide;
    const bar = mk('div', 'vx-seekbar');
    bar.innerHTML = `<div class="vx-seekbar__track"><div class="vx-seekbar__fill"></div><div class="vx-seekbar__thumb"></div></div><div class="vx-seekbar__time"><span class="vx-seekbar__cur">0:00</span><span class="vx-seekbar__dur">0:00</span></div>`;
    inner.appendChild(bar);
    const fill = q('.vx-seekbar__fill', bar), thumb = q('.vx-seekbar__thumb', bar);
    const cur  = q('.vx-seekbar__cur',  bar), dur   = q('.vx-seekbar__dur',   bar);

    const updateBar = () => { if (!v?.duration) return; const p = (v.currentTime/v.duration)*100; fill.style.width = p+'%'; thumb.style.left = p+'%'; cur.textContent = fmtTime(v.currentTime); dur.textContent = fmtTime(v.duration); };
    if (v) { v.addEventListener('timeupdate', updateBar); v.addEventListener('loadedmetadata', () => { dur.textContent = fmtTime(v.duration); }); }

    let dragging = false, wasPaused = false;
    const seekFromX = x => { if (!v?.duration) return; const r = bar.getBoundingClientRect(); v.currentTime = Math.min(1, Math.max(0, (x-r.left)/r.width)) * v.duration; updateBar(); };
    const startDrag = x => { dragging=true; bar.classList.add('is-dragging'); if(v){wasPaused=v.paused; if(!v.paused)v.pause();} seekFromX(x); };
    const moveDrag  = x => { if(dragging) seekFromX(x); };
    const endDrag   = ()=> { if(!dragging)return; dragging=false; bar.classList.remove('is-dragging'); if(v&&!wasPaused)v.play().catch(()=>{}); };

    bar.addEventListener('mousedown',  e => { e.stopPropagation(); startDrag(e.clientX); });
    document.addEventListener('mousemove', e => { if(dragging){e.stopPropagation(); moveDrag(e.clientX);} });
    document.addEventListener('mouseup',   ()=> { if(dragging) endDrag(); });
    bar.addEventListener('touchstart', e => { e.stopPropagation(); startDrag(e.touches[0].clientX); }, {passive:true});
    bar.addEventListener('touchmove',  e => { e.stopPropagation(); moveDrag(e.touches[0].clientX); },  {passive:true});
    bar.addEventListener('touchend',   ()=> endDrag(), {passive:true});
  }

  function createControls(slide) {
    if (q('.vx-ctrl-overlay', slide)) return;
    const inner = q('.vx-modal__slideInner', slide) || slide;
    inner.style.position = 'relative';
    const loader = mk('div', 'vx-slide-loader'); loader.innerHTML = `<div class="vx-slide-loader__spinner"></div>`; inner.appendChild(loader);
    const overlay = mk('div', 'vx-ctrl-overlay');
    const center  = mk('div', 'vx-ctrl-center');
    const btnBack = mk('button', 'vx-ctrl-btn', SVG.rewind  + `<span class="vx-ctrl-btn__label">3s</span>`);
    const btnPlay = mk('button', 'vx-ctrl-btn vx-ctrl-btn--play', SVG.pause);
    const btnFwd  = mk('button', 'vx-ctrl-btn', SVG.forward + `<span class="vx-ctrl-btn__label">3s</span>`);
    btnBack.setAttribute('aria-label', '-3s'); btnPlay.setAttribute('aria-label', 'Play/Pause'); btnFwd.setAttribute('aria-label', '+3s');
    center.append(btnPlay); overlay.append(center); inner.appendChild(overlay);
    createSeekbar(slide); 
    const v = q('video', slide);
    const syncIcon = () => { btnPlay.innerHTML = (v&&!v.paused) ? SVG.pause : SVG.play; };
    if (v) ['play','pause','ended'].forEach(ev => v.addEventListener(ev, syncIcon));
    if (v) { v.addEventListener('waiting', ()=>showLoader(slide)); v.addEventListener('canplay', ()=>hideLoader(slide)); v.addEventListener('playing', ()=>hideLoader(slide)); v.addEventListener('stalled', ()=>showLoader(slide)); }
    btnBack.addEventListener('click', e => { e.stopPropagation(); if(v) v.currentTime=Math.max(0,v.currentTime-3); showControls(slide,true); });
    btnFwd.addEventListener( 'click', e => { e.stopPropagation(); if(v) v.currentTime=Math.min(v.duration||0,v.currentTime+3); showControls(slide,true); });
    btnPlay.addEventListener('click', e => { e.stopPropagation(); if(!v)return; v.paused?v.play().catch(()=>{}):v.pause(); showControls(slide,!v.paused); });
    if (v) v.addEventListener('ended', ()=>hideControls(slide));
  }

  function showControls(slide, withTimer) {
    const center = q('.vx-ctrl-center', slide); if(!center)return;
    center.classList.add('is-visible');
    if (ctrlTimers.has(slide)) { clearTimeout(ctrlTimers.get(slide)); ctrlTimers.delete(slide); }
    if (withTimer) {
      const v = q('video', slide);
      ctrlTimers.set(slide, setTimeout(()=>{ if(v&&v.paused)return; center.classList.remove('is-visible'); ctrlTimers.delete(slide); }, 3000));
    }
  }

  function hideControls(slide) {
    const center = q('.vx-ctrl-center', slide); if(!center)return;
    if (ctrlTimers.has(slide)) { clearTimeout(ctrlTimers.get(slide)); ctrlTimers.delete(slide); }
    center.classList.remove('is-visible');
  }

  function showTapIcon(slide, didPause) {
    const inner = q('.vx-modal__slideInner', slide) || slide;
    const existing = q('.vx-tap-icon', inner);
    if (existing) { existing.remove(); }
    const icon = document.createElement('div');
    icon.className = 'vx-tap-icon';
    icon.innerHTML = didPause ? SVG.pause : SVG.play;
    inner.appendChild(icon);
    icon.addEventListener('animationend', () => icon.remove(), { once: true });
  }

  /* ============================
     MODAL
  ============================ */
  const state = { stories:[], index:0, muted:false, modal:null, rail:null, bar:null, soundBtn:null, navUp:null, navDown:null, tagsEl:null, blockTags:'', showLike:true, showShare:true, rafId:0, wheelLock:false, sourcesLoaded:false };

  function getStoryList(scope) {
    return qa('.vx-story-vid', scope || document)
      .filter(wrap => !wrap.closest('.swiper-slide-duplicate')) // exclure les clones Swiper
      .map(wrap => {
        const btn = q('.vx-story-open', wrap), v = q('video', wrap);
        const src = btn?.dataset.video || q('source',v)?.getAttribute('src') || v?.currentSrc || '';
        return { wrap, src, url: btn?.dataset.url || '#', likeCount: btn?.dataset.likeCount || '', authorAvatar: btn?.dataset.authorAvatar || '', authorName: btn?.dataset.authorName || '', authorDesc: btn?.dataset.authorDesc || '' };
      }).filter(x => x.src);
  }

  function cacheModalEls() {
    state.modal    = q('.vx-modal'); if(!state.modal) return false;
    state.rail     = q('.vx-modal__rail',                  state.modal);
    state.soundBtn = q('.vx-modal__sound',                 state.modal);
    state.navUp    = q('.vx-modal__navbtn[data-dir="-1"]', state.modal);
    state.navDown  = q('.vx-modal__navbtn[data-dir="1"]',  state.modal);
    state.tagsEl   = q('.vx-modal__tags',                  state.modal);
    return true;
  }

  const LIKE_OUTLINE_SVG = `<svg class="vx-like-outline" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#fff"><path d="M9.221 1.795a1 1 0 011.109-.656l1.04.173a4 4 0 013.252 4.784L14 9h4.061a3.664 3.664 0 013.576 2.868A3.68 3.68 0 0121 14.85l.02.087A3.815 3.815 0 0120 18.5v.043l-.01.227a2.82 2.82 0 01-.135.663l-.106.282A3.754 3.754 0 0116.295 22h-3.606l-.392-.007a12.002 12.002 0 01-5.223-1.388l-.343-.189-.27-.154a2.005 2.005 0 00-.863-.26l-.13-.004H3.5a1.5 1.5 0 01-1.5-1.5V12.5A1.5 1.5 0 013.5 11h1.79l.157-.013a1 1 0 00.724-.512l.063-.145 2.987-8.535Zm-1.1 9.196A3 3 0 015.29 13H4v4.998h1.468a4 4 0 011.986.528l.27.155.285.157A10 10 0 0012.69 20h3.606c.754 0 1.424-.483 1.663-1.2l.03-.126a.819.819 0 00.012-.131v-.872l.587-.586c.388-.388.577-.927.523-1.465l-.038-.23-.02-.087-.21-.9.55-.744A1.663 1.663 0 0018.061 11H14a2.002 2.002 0 01-1.956-2.418l.623-2.904a2 2 0 00-1.626-2.392l-.21-.035-2.71 7.741Z"/></svg>`;
  const LIKE_FILLED_SVG  = `<svg class="vx-like-filled" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#3ea6ff"><path d="M10.72 2.18a3.263 3.263 0 012.352 4.063l-.708 2.476a1 1 0 00.962 1.275h5.29c.848 0 1.624.48 2.003 1.238l.179.359a1.785 1.785 0 01-.6 2.279.446.446 0 00-.198.37v.07c0 .124.041.246.116.346a2.375 2.375 0 01-.41 3.278l-.5.399a.38.38 0 00-.123.416l.07.206c.217.653.1 1.372-.313 1.923a2.8 2.8 0 01-2.24 1.12l-3.914-.002a12 12 0 01-5.952-1.584l-.272-.155a2.002 2.002 0 00-.993-.265H3a1 1 0 01-1-1v-5.996a1 1 0 011.002-1L5.789 12a1 1 0 00.945-.67l3.02-8.628a.816.816 0 01.967-.523Z"/></svg>`;
  const SHARE_SVG        = `<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#fff"><path d="M10 3.158V7.51c-5.428.223-8.27 3.75-8.875 11.199-.04.487-.07.975-.09 1.464l-.014.395c-.014.473.578.684.88.32.302-.368.61-.73.925-1.086l.244-.273c1.79-1.967 3-2.677 4.93-2.917a18.011 18.011 0 012-.112v4.346a1 1 0 001.646.763l9.805-8.297 1.55-1.31-1.55-1.31-9.805-8.297A1 1 0 0010 3.158Zm2 6.27v.002-4.116l7.904 6.688L12 18.689v-4.212l-2.023.024c-1.935.022-3.587.17-5.197 1.024a9 9 0 00-1.348.893c.355-1.947.916-3.39 1.63-4.425 1.062-1.541 2.607-2.385 5.02-2.485L12 9.428Z"></path></svg>`;

  function buildRail() {
    state.rail.innerHTML = '';
    state.stories.forEach((item, i) => {
      const slide = mk('div', 'vx-modal__slide'); slide.dataset.index = i;
      const card    = item.wrap?.querySelector('.vx-story-card');
      const ctaTitle = card?.querySelector('.vx-story-card__title')?.textContent.trim() ?? '';
      const ctaPrice = card?.querySelector('.vx-story-card__price')?.innerHTML ?? '';
      const ctaImgEl = card?.querySelector('.vx-story-card__thumb img');
      const ctaSrc   = ctaImgEl?.getAttribute('src') ?? '';
      const ctaAlt   = ctaImgEl?.getAttribute('alt') ?? '';
      const liked    = isLiked(item.src);

      const authorHTML = item.authorName ? `
        <div class="vx-modal__author">
          ${item.authorAvatar ? `<img class="vx-modal__author-avatar" src="${item.authorAvatar}" alt="" width="40" height="40" loading="lazy">` : ''}
          <div class="vx-modal__author-info"><span class="vx-modal__author-name">${item.authorName}</span></div>
          ${item.authorDesc ? `<div class="vx-modal__author-info" style="width:100%"><span class="vx-modal__author-desc">${item.authorDesc}</span></div>` : ''}
        </div>` : '';

      const actionsHTML = (state.showLike || state.showShare) ? `
        <div class="vx-modal__actions">
          ${state.showLike ? `<button class="vx-modal__action-btn vx-modal__like${liked ? ' is-liked' : ''}" aria-label="J'aime">${LIKE_OUTLINE_SVG}${LIKE_FILLED_SVG}<span class="vx-modal__like-count">${item.likeCount || ''}</span></button>` : ''}
          ${state.showShare ? `<button class="vx-modal__action-btn vx-modal__share" aria-label="Partager">${SHARE_SVG}</button>` : ''}
        </div>` : '';

      const ctaHTML = ctaTitle ? `
        <a class="vx-modal__cta" href="${item.url || '#'}" target="_self">
          <div class="vx-modal__ctaThumb"><img class="vx-modal__ctaImg" src="${ctaSrc}" alt="${ctaAlt}" loading="lazy" style="opacity:1"></div>
          <div class="vx-modal__ctaInfo"><div class="vx-modal__ctaTitle">${ctaTitle}</div><div class="vx-modal__ctaPrice">${ctaPrice}</div></div>
        </a>` : '';

      slide.innerHTML = `<div class="vx-modal__slideInner"><video playsinline preload="auto"></video></div>${authorHTML}${actionsHTML}${ctaHTML}`;
      state.rail.appendChild(slide);
    });
    state.rail.style.setProperty('--vx-story-count', state.stories.length);
  }

  function loadAllVideoSourcesOnce() {
    qa('.vx-modal__slide', state.modal).forEach(slide => {
      const v = q('video', slide), item = state.stories[+slide.dataset.index];
      if (!v||!item) return;
      v.loop=true; v.controls=false; v.playsInline=true; v.muted=true;
      if (!v.src) { v.src=item.src; v.preload='auto'; v.load(); }
      Object.assign(v.style, {transition:'opacity 180ms linear', opacity:'1'});
      createControls(slide);
    });
    state.sourcesLoaded = true;
  }

  const getActiveSlide = () => q(`.vx-modal__slide[data-index="${state.index}"]`, state.modal);
  const getActiveVideo = () => { const s=getActiveSlide(); return s ? q('video',s) : null; };

  function applyMuteToAll(muted) {
    state.muted = muted;
    qa('.vx-modal__slide video', state.modal).forEach(v => v.muted = muted);
    if (state.soundBtn) {
      state.soundBtn.classList.toggle('is-muted', muted);
      state.soundBtn.setAttribute('aria-label', muted ? 'Activer son' : 'Couper son');
    }
  }

  function updateCTAFromStory() {
    if(!state.cta)return;
    const wrap=state.stories[state.index]?.wrap, card=wrap?.querySelector('.vx-story-card'); if(!card)return;
    const get=s=>card.querySelector(s);
    const ctaImg=q('.vx-modal__ctaImg',state.cta), ctaTitle=q('.vx-modal__ctaTitle',state.cta), ctaPrice=q('.vx-modal__ctaPrice',state.cta);
    if(ctaTitle) ctaTitle.textContent=get('.vx-story-card__title')?.textContent.trim()??'';
    if(ctaPrice) ctaPrice.innerHTML=get('.vx-story-card__price')?.innerHTML??'';
    if(ctaImg){const imgEl=get('.vx-story-card__thumb img'), src=imgEl?.getAttribute('src')??''; src?Object.assign(ctaImg,{src,alt:imgEl?.getAttribute('alt')??''},{style:'opacity:1'}):(ctaImg.removeAttribute('src'),ctaImg.style.opacity='0');}
  }

  function updateAuthorFromStory() {
    if(!state.authorEl) return;
    const item = state.stories[state.index];
    const avatar = item?.authorAvatar || '';
    const name   = item?.authorName   || '';
    const desc   = item?.authorDesc   || '';
    state.authorEl.style.display = name ? '' : 'none';
    const avatarEl = q('.vx-modal__author-avatar', state.authorEl);
    const nameEl   = q('.vx-modal__author-name',   state.authorEl);
    const descEl   = q('.vx-modal__author-desc',   state.authorEl);
    if (avatarEl) { if (avatar) { avatarEl.src = avatar; avatarEl.style.display = ''; } else { avatarEl.style.display = 'none'; } }
    if (nameEl)  nameEl.textContent  = name;
    if (descEl)  { descEl.innerHTML = desc; descEl.style.display = desc ? '' : 'none'; }
  }

  function updateTagsFromStory() {
    if(!state.tagsEl)return;
    const tagsStr = state.blockTags || '';
    if(!tagsStr.trim()) { state.tagsEl.innerHTML = ''; return; }
    const tags = tagsStr.split(',').map(t => t.trim()).filter(Boolean);
    state.tagsEl.innerHTML = tags.map(entry => {
      const [label, iconUrl = '', linkUrl = ''] = entry.split('|');
      const img = iconUrl ? `<img src="${iconUrl}" alt="" width="14" height="14" style="object-fit:contain;flex-shrink:0;">` : '';
      const tagContent = `${img}<span>${label}</span>`;
      
      // Si un lien est fourni, créer un élément <a>, sinon un <span>
      if (linkUrl && linkUrl.trim()) {
        return `<a href="${linkUrl}" class="vx-modal__tag" rel="noopener noreferrer">${tagContent}</a>`;
      } else {
        return `<span class="vx-modal__tag">${tagContent}</span>`;
      }
    }).join('');
  }

  /* ── Like / Share ─────────────────────────────────────── */
  const getLikeKey = src => {
    try {
      // Créer une clé basée sur l'URL complète pour éviter les conflits
      const hash = Array.from(src).reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0) | 0, 0).toString(36);
      return 'vx_like_' + hash;
    } catch (_) {
      return 'vx_like_default';
    }
  };
  const isLiked    = src => { try { return localStorage.getItem(getLikeKey(src)) === '1'; } catch(_) { return false; } };
  const toggleLike = src => { try { const k=getLikeKey(src); localStorage.getItem(k)==='1'?localStorage.removeItem(k):localStorage.setItem(k,'1'); } catch(_) {} };

  function handleLike() {
    const item = state.stories[state.index]; if(!item) return;
    toggleLike(item.src);
    const likeBtn = q('.vx-modal__like', getActiveSlide()); if(!likeBtn) return;
    const liked = isLiked(item.src);
    likeBtn.classList.toggle('is-liked', liked);
    likeBtn.classList.add('vx-like-pop');
    likeBtn.addEventListener('animationend', () => likeBtn.classList.remove('vx-like-pop'), { once: true });
  }

  function handleShare() {
    const item = state.stories[state.index];
    const url  = item?.url && item.url !== '#' ? window.location.origin + item.url : window.location.href;
    if (navigator.share) {
      navigator.share({ url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).catch(() => {});
    }
  }

  function setActiveSlide() {
    if (isMobile()) {
      const h = window.innerHeight;
      state.rail.style.transition = 'transform 380ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      state.rail.style.transform = `translate3d(0,${-state.index * h}px,0)`;
    } else {
      state.rail.style.transition = 'transform 320ms cubic-bezier(0.4, 0, 0.2, 1)';
      state.rail.style.transform = `translate3d(0,${-state.index * 100}%,0)`;
    }

    qa('.vx-modal__slide', state.modal).forEach(s => {
      s.removeAttribute('data-active');
      hideControls(s);
    });

    const slide = getActiveSlide();
    if (slide) slide.setAttribute('data-active', '1');

    updateTagsFromStory();

    const v = getActiveVideo();
    if (!v) return;

    // Pause + reset toutes les autres vidéos
    qa('.vx-modal__slide video', state.modal).forEach(x => {
      if (x !== v) { x.pause(); x.currentTime = 0; }
    });

    v.muted = state.muted;
    v.currentTime = 0;

    // Forcer le load si src pas encore chargée
    if (!v.src || v.readyState === 0) {
      const item = state.stories[state.index];
      if (item) { v.src = item.src; v.load(); }
    }

    if (v.readyState >= 2) {
      hideLoader(slide);
      v.play().catch(() => {});
    } else {
      showLoader(slide);
      const onReady = () => {
        hideLoader(slide);
        if (getActiveSlide() === slide) v.play().catch(() => {});
      };
      v.addEventListener('canplay',    onReady, { once: true });
      v.addEventListener('loadeddata', onReady, { once: true });
    }

    v.onended = () => state.index >= state.stories.length - 1
      ? (state.index = 0, setActiveSlide())
      : goTo(1);

    if (state.navUp)   state.navUp.disabled   = state.index <= 0;
    if (state.navDown) state.navDown.disabled = state.index >= state.stories.length - 1;
  }

  function openModalFor(btn) {
    if (!cacheModalEls()) return;
    const scope = btn.closest('.vx-stories') || document;
    state.blockTags  = scope.dataset?.blockTags || '';
    state.showLike   = scope.dataset?.showLike  !== 'false';
    state.showShare  = scope.dataset?.showShare !== 'false';
    state.stories    = getStoryList(scope);
    if (!state.stories.length) return;
    const wrap = btn.closest('.vx-story-vid');
    state.index = Math.max(0, state.stories.findIndex(x => x.wrap === wrap));
    state.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    buildRail();
    loadAllVideoSourcesOnce();
    applyMuteToAll(false);
    try { wrap?.querySelector('video')?.pause(); } catch(_) {}
    const isiOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isiOS) {
      const activeSlideEl = q(`.vx-modal__slide[data-index="${state.index}"]`, state.modal);
      const vios = activeSlideEl ? q('video', activeSlideEl) : null;
      if (vios) { vios.muted = false; vios.currentTime = 0; vios.play().catch(() => {}); }
    }
    setActiveSlide();
    startProgressLoop();
    setTimeout(() => {
      const v = getActiveVideo();
      if (v && v.paused) v.play().catch(() => {});
    }, 80);
  }

  function closeModal(skipResume = false) {
    if(!state.modal)return; stopProgressLoop();
    qa('.vx-modal__slide video',state.modal).forEach(v=>v.pause());
    state.modal.hidden=true; document.body.style.overflow='';
    if(!skipResume) try{state.stories?.[state.index]?.wrap?.querySelector('video')?.play().catch(()=>{});}catch(_){}
  }

  /* ── Mini player ──────────────────────────────────────── */
  let _pip = null, _pipPlayHandler = null;

  function closeMiniPlayer() {
    if(!_pip) return;
    if(_pipPlayHandler) { document.removeEventListener('play', _pipPlayHandler, true); _pipPlayHandler = null; }
    _pip.classList.add('is-closing');
    _pip.addEventListener('animationend', () => { _pip?.remove(); _pip = null; }, { once: true });
  }

  function makeDraggable(el) {
    let sx, sy, sl, st, dragging = false, moved = false;

    el.addEventListener('pointerdown', e => {
      if(e.target.closest('.vx-miniplayer__btn,.vx-miniplayer__rz')) return;
      dragging = true; moved = false;
      sx = e.clientX; sy = e.clientY;
      const r = el.getBoundingClientRect();
      sl = r.left; st = r.top;
      el.style.right = 'auto'; el.style.bottom = 'auto';
      el.style.left = sl + 'px'; el.style.top = st + 'px';
      el.style.transition = 'none';
      el.classList.add('is-dragging');
      el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', e => {
      if(!dragging) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      if(Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
      const W = window.innerWidth, H = window.innerHeight;
      const w = el.offsetWidth, h = el.offsetHeight;
      el.style.left = Math.max(0, Math.min(W - w, sl + dx)) + 'px';
      el.style.top  = Math.max(0, Math.min(H - h, st + dy)) + 'px';
    });

    el.addEventListener('pointerup', () => {
      if(!dragging) return;
      const wasMoved = moved;
      dragging = false; moved = false;
      el.classList.remove('is-dragging');
      if(wasMoved) {
        el.addEventListener('click', e => e.stopImmediatePropagation(), { once: true, capture: true });
      }
    });
  }

  function makeResizable(el) {
    const MIN_W = 80, MAX_W = 500;
    const dirs = {
      n:  { t: true,  b: false, l: false, r: false },
      s:  { t: false, b: true,  l: false, r: false },
      w:  { t: false, b: false, l: true,  r: false },
      e:  { t: false, b: false, l: false, r: true  },
      nw: { t: true,  b: false, l: true,  r: false },
      ne: { t: true,  b: false, l: false, r: true  },
      sw: { t: false, b: true,  l: true,  r: false },
      se: { t: false, b: true,  l: false, r: true  },
    };
    Object.entries(dirs).forEach(([dir, f]) => {
      const h = el.querySelector(`.vx-miniplayer__rz--${dir}`);
      if (!h) return;
      let x0, y0, w0, h0, l0, t0, ratio;
      h.addEventListener('pointerdown', e => {
        e.stopPropagation();
        x0 = e.clientX; y0 = e.clientY;
        const r = el.getBoundingClientRect();
        w0 = r.width; h0 = r.height; l0 = r.left; t0 = r.top;
        ratio = w0 / h0;
        el.style.right = 'auto'; el.style.bottom = 'auto';
        el.style.left = l0 + 'px'; el.style.top = t0 + 'px';
        el.style.transition = 'none';
        h.setPointerCapture(e.pointerId);
      });
      h.addEventListener('pointermove', e => {
        const dx = e.clientX - x0, dy = e.clientY - y0;
        let nl = l0, nt = t0;
        // Calcule un seul delta en largeur pour tous les cas, dérive la hauteur via le ratio
        let delta;
        if      (f.r && f.b)  delta = (dx + dy * ratio) / 2;
        else if (f.l && f.b)  delta = (-dx + dy * ratio) / 2;
        else if (f.r && f.t)  delta = (dx - dy * ratio) / 2;
        else if (f.l && f.t)  delta = (-dx - dy * ratio) / 2;
        else if (f.r)         delta = dx;
        else if (f.l)         delta = -dx;
        else if (f.b)         delta = dy * ratio;
        else                  delta = -dy * ratio; // t seul
        const nw = Math.max(MIN_W, Math.min(MAX_W, w0 + delta));
        const nh = nw / ratio;
        if (f.l) nl = l0 + w0 - nw;
        if (f.t) nt = t0 + h0 - nh;
        el.style.width = nw + 'px'; el.style.height = nh + 'px';
        el.style.left  = nl + 'px'; el.style.top    = nt + 'px';
      });
      h.addEventListener('pointerup', () => {
        el.addEventListener('click', e => e.stopImmediatePropagation(), { once: true, capture: true });
      });
    });
  }

  function openMiniPlayer() {
    const v = getActiveVideo(), item = state.stories[state.index];
    if(!v || !item) return;
    closeMiniPlayer();

    const pip = document.createElement('div');
    pip.className = 'vx-miniplayer';
    pip.dataset.idx = state.index;

    const inner = document.createElement('div');
    inner.className = 'vx-miniplayer__inner';

    const vid = document.createElement('video');
    vid.src = v.src || item.src;
    vid.currentTime = v.currentTime;
    vid.muted = state.muted;
    vid.loop = true;
    vid.playsInline = true;

    const controls = document.createElement('div');
    controls.className = 'vx-miniplayer__controls';

    const soundBtn = document.createElement('button');
    soundBtn.className = 'vx-miniplayer__btn vx-miniplayer__btn-sound';
    soundBtn.setAttribute('aria-label', state.muted ? 'Activer son' : 'Couper son');
    if(state.muted) soundBtn.classList.add('is-muted');
    soundBtn.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path class="speaker" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"></path>
      <path class="mute-slash" d="M2 2l20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0"></path>
    </svg>`;

    const expandBtn = document.createElement('button');
    expandBtn.className = 'vx-miniplayer__btn vx-miniplayer__btn-expand';
    expandBtn.setAttribute('aria-label', 'Agrandir');
    expandBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"></path></svg>`;

    controls.append(soundBtn, expandBtn);
    inner.append(vid, controls);

    ['n','s','e','w','nw','ne','sw','se'].forEach(dir => {
      const h = document.createElement('div');
      h.className = `vx-miniplayer__rz vx-miniplayer__rz--${dir}`;
      pip.appendChild(h);
    });

    pip.appendChild(inner);
    document.body.appendChild(pip);
    _pip = pip;
    vid.play().catch(() => {});

    soundBtn.addEventListener('click', e => {
      e.stopPropagation();
      state.muted = !state.muted;
      vid.muted = state.muted;
      soundBtn.setAttribute('aria-label', state.muted ? 'Activer son' : 'Couper son');
      soundBtn.classList.toggle('is-muted', state.muted);
      vid.muted = state.muted;
    });

    expandBtn.addEventListener('click', e => {
      e.stopPropagation();
      if(!state.modal) return;
      const t = vid.currentTime;
      vid.pause();
      if(_pipPlayHandler) { document.removeEventListener('play', _pipPlayHandler, true); _pipPlayHandler = null; }
      closeMiniPlayer();
      // Le rail est déjà construit — on réaffiche simplement le modal sans reconstruire
      state.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      applyMuteToAll(state.muted);
      setActiveSlide();
      startProgressLoop();
      setTimeout(() => { const av = getActiveVideo(); if(av) { av.currentTime = t; av.play().catch(() => {}); } }, 30);
    });

    pip.addEventListener('click', e => {
      if(e.target.closest('.vx-miniplayer__btn,.vx-miniplayer__rz')) return;
      if(!state.modal) return;
      const t = vid.currentTime;
      vid.pause();
      if(_pipPlayHandler) { document.removeEventListener('play', _pipPlayHandler, true); _pipPlayHandler = null; }
      closeMiniPlayer();
      // Le rail est déjà construit — on réaffiche simplement le modal sans reconstruire
      state.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      applyMuteToAll(state.muted);
      setActiveSlide();
      startProgressLoop();
      setTimeout(() => { const av = getActiveVideo(); if(av) { av.currentTime = t; av.play().catch(() => {}); } }, 30);
    });

    _pipPlayHandler = e => {
      if(e.target !== vid && e.target.tagName === 'VIDEO') vid.pause();
    };
    document.addEventListener('play', _pipPlayHandler, true);

    makeDraggable(pip);
    if (!isMobile()) makeResizable(pip);
    closeModal(true);
  }

  function goTo(dir) { const next=state.index+dir; if(next<0||next>=state.stories.length)return; state.index=next; setActiveSlide(); }

  function startProgressLoop() {
    stopProgressLoop();
    const tick=()=>{ if(!state.modal||state.modal.hidden)return; const v=getActiveVideo(); if(state.bar){state.bar.style.width=v?.duration?(v.currentTime/v.duration*100)+'%':'0%';} state.rafId=requestAnimationFrame(tick); };
    state.rafId=requestAnimationFrame(tick);
  }
  function stopProgressLoop(){if(state.rafId){cancelAnimationFrame(state.rafId);state.rafId=0;}}

  const tap={t:0,x:0};
  function doSeek(clientX,slide){const rect=slide.getBoundingClientRect(),left=(clientX-rect.left)<rect.width/2,v=q('video',slide);if(v)v.currentTime=left?Math.max(0,v.currentTime-3):Math.min(v.duration||0,v.currentTime+3);showControls(slide,true);}

  document.addEventListener('click', e=>{
    const open=e.target.closest('.vx-story-open');
    if(open&&!open.closest('.swiper-slide-duplicate')){e.preventDefault();openModalFor(open);return;}
    if(!state.modal||state.modal.hidden)return;
    if(e.target.closest('.vx-modal__close,.vx-modal__backdrop')){closeModal();return;}
    const pipBtn=e.target.closest('.vx-modal__pip'); if(pipBtn){openMiniPlayer();return;}
    const soundBtn=e.target.closest('.vx-modal__sound'); if(soundBtn){applyMuteToAll(!state.muted);getActiveVideo()?.play().catch(()=>{});return;}
    const navBtn=e.target.closest('.vx-modal__navbtn'); if(navBtn){goTo(+navBtn.dataset.dir);return;}
    const likeBtn=e.target.closest('.vx-modal__like'); if(likeBtn){handleLike();return;}
    const shareBtn=e.target.closest('.vx-modal__share'); if(shareBtn){handleShare();return;}
  });

  document.addEventListener('pointerdown', e=>{
    if(!state.modal||state.modal.hidden)return;
    if(e.target.closest('.vx-ctrl-btn,.vx-seekbar,.vx-modal__close,.vx-modal__backdrop,.vx-modal__sound,.vx-modal__pip,.vx-modal__navbtn,.vx-story-open,.vx-modal__action-btn'))return;
    const slide=e.target.closest('.vx-modal__slide'); if(!slide)return;
    const now=Date.now(), dx=Math.abs(e.clientX-tap.x);
    if(now-tap.t<280&&dx<50){tap.t=0;doSeek(e.clientX,slide);}
    else{tap.t=now;tap.x=e.clientX;}
  });

  document.addEventListener('pointerup', e=>{
    if(!state.modal||state.modal.hidden)return;
    if(e.target.closest('.vx-ctrl-btn,.vx-seekbar,.vx-modal__close,.vx-modal__backdrop,.vx-modal__sound,.vx-modal__pip,.vx-modal__navbtn,.vx-story-open,.vx-modal__action-btn'))return;
    if(touch.moved)return; // scroll en cours — ne pas toggle
    const slide=e.target.closest('.vx-modal__slide'); if(!slide)return;
    const v=q('video',slide); if(!v)return;
    const wasPlaying=!v.paused;
    wasPlaying?v.pause():v.play().catch(()=>{});
    showTapIcon(slide,wasPlaying);
  });

  document.addEventListener('wheel', e=>{
    if(!state.modal||state.modal.hidden||state.wheelLock)return;
    if(e.target.closest('.vx-modal__tags'))return;
    state.wheelLock=true; setTimeout(()=>state.wheelLock=false,300);
    if(e.deltaY>20)goTo(1); else if(e.deltaY<-20)goTo(-1);
  },{passive:true});

  const touch = {y:0, x:0, moved:false, startTranslate:0, velY:0, lastY:0, lastT:0};

  document.addEventListener('touchstart', e => {
    if(!state.modal||state.modal.hidden||e.target.closest('.vx-seekbar')) return;
    touch.y = e.touches[0].clientY;
    touch.x = e.touches[0].clientX;
    touch.moved = false;
    touch.velY = 0;
    touch.lastY = touch.y;
    touch.lastT = Date.now();
    if(isMobile()) {
      touch.startTranslate = -state.index * window.innerHeight;
      state.rail.style.transition = 'none';
    }
  }, {passive:true});

  document.addEventListener('touchmove', e => {
    if(!state.modal||state.modal.hidden) return;
    const cy = e.touches[0].clientY, cx = e.touches[0].clientX;
    if(Math.abs(cy-touch.y)>8||Math.abs(cx-touch.x)>8) touch.moved = true;
    if(!isMobile()||e.target.closest('.vx-seekbar,.vx-modal__tags')) return;
    const now = Date.now(), dt = now - touch.lastT;
    if(dt > 0) touch.velY = (cy - touch.lastY) / dt;
    touch.lastY = cy; touch.lastT = now;
    const h = window.innerHeight;
    const maxT = 0, minT = -(state.stories.length - 1) * h;
    let tr = touch.startTranslate + (cy - touch.y);
    if(tr > maxT)      tr = maxT + (tr - maxT) * 0.18;
    else if(tr < minT) tr = minT + (tr - minT) * 0.18;
    state.rail.style.transform = `translate3d(0,${tr}px,0)`;
  }, {passive:true});

  document.addEventListener('touchend', e => {
    if(!state.modal||state.modal.hidden||e.target.closest('.vx-seekbar')) return;
    const t = e.changedTouches[0], diffY = t.clientY-touch.y, diffX = t.clientX-touch.x;
    if(touch.moved) {
      if(isMobile()) {
        const h = window.innerHeight;
        if((Math.abs(diffY) > h*0.22 || Math.abs(touch.velY) > 0.35) && Math.abs(diffY) > Math.abs(diffX)) {
          goTo(diffY < 0 ? 1 : -1);
        } else {
          state.rail.style.transition = 'transform 320ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          state.rail.style.transform = `translate3d(0,${-state.index * h}px,0)`;
        }
        return;
      }
      if(Math.abs(diffY)>60&&Math.abs(diffY)>Math.abs(diffX)) goTo(diffY<0?1:-1);
    }
  }, {passive:true});

  document.addEventListener('keydown', e=>{
    if(!state.modal||state.modal.hidden)return;
    const slide=getActiveSlide(),v=getActiveVideo();
    if(e.key==='Escape'){closeModal();return;} if(e.key==='ArrowDown'){goTo(1);return;} if(e.key==='ArrowUp'){goTo(-1);return;}
    if(e.key===' '||e.key==='k'){e.preventDefault();if(!v)return;v.paused?v.play().catch(()=>{}):v.pause();if(slide)showControls(slide,!v.paused);}
    if((e.key==='ArrowLeft'||e.key==='ArrowRight')&&v){e.preventDefault();const l=e.key==='ArrowLeft';v.currentTime=l?Math.max(0,v.currentTime-3):Math.min(v.duration||0,v.currentTime+3);if(slide)showControls(slide,true);}
  });

  document.addEventListener('fullscreenchange',()=>{const fs=!!(document.fullscreenElement||document.webkitFullscreenElement);if(state.modal)qa('.vx-modal__slide video',state.modal).forEach(v=>v.controls=fs);});

  /* ── Init ─────────────────────────────────────────────────── */
  function lazyInitSwipers() {
    // rootMargin: '0px' → Swiper s'init seulement quand la section est vraiment visible
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        obs.unobserve(e.target);
        const swiperEl = e.target.querySelector('.vx-swiper');
        if (swiperEl && swiperEl.dataset.vxInit !== '1') initSwiper(swiperEl);
      });
    }, { rootMargin: '0px', threshold: 0.01 });

    qa('.vx-stories').forEach(el => io.observe(el));
  }

  const _boot = () => { injectCSS(); cacheModalEls(); };
  document.addEventListener('DOMContentLoaded', _boot);
  if (document.readyState !== 'loading') _boot();

  // Swiper + vidéos : uniquement après window.load complet
  if (document.readyState === 'complete') {
    lazyInitSwipers();
  } else {
    window.addEventListener('load', lazyInitSwipers, { once: true });
  }
})();